import { CreatePaymentStatus } from '@/store/slices/stagesSlice/interfaces'
import { checkPayment, getPaymentState } from '@/store/slices/stagesSlice/stagesSlice'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Container, Button, FixedBottom, Icon } from '@/components'
import { COUNT_REQUEST_PAYMENT_PENDING, DELAY_PAYMENT_PENDING, NUMBER_CAR, supportPhoneLink } from '@/constants'
import styles from './PaymentPendingPage.module.scss'
import getLinkHref from '@/helpers/getLinkHref'
import { RootState } from '@/store'
import { STATUS_PAYMENT_MESSAGES } from './PaymentPendingPage.constants'
import { useFetchPaymentStatus } from '@/helpers/hooks/useFetchPaymentStatus'
import { getCookie } from 'cookies-next'

export default function PaymentPendingPage({
  orderNumber,
  subscriptionId,
  eventId,
  fineId,
}: {
  orderNumber: string
  subscriptionId?: string
  eventId?: string
  fineId?: string
}) {
  const [status, setStatus] = useState<CreatePaymentStatus | 'exhausted'>('pending')
  const [loading, setLoading] = useState(false)
  const [formUrl, setFormUrl] = useState<string | null>(null)
  const dispatch = useDispatch()
  const router = useRouter()
  const reduxState = useSelector<RootState, RootState>(state => state)
  const { data } = getPaymentState(reduxState)
  const hrefFindCar = getLinkHref({ type: 'find-car-number', data: { id: data.area?.id } })
  const hrefMainPage = getLinkHref({ type: 'area', data: { id: data.area?.id } })
  const hrefSubscriptionFindCar = subscriptionId
    ? getLinkHref({ type: 'subscription-number', data: { areaId: data.area?.id, subscriptionId } })
    : ''

  const carNumber = String(getCookie(NUMBER_CAR) || '')
  const hrefEventFindCar = eventId
    ? getLinkHref({ type: 'event-number', data: { areaId: data.area?.id, eventId } })
    : ''
  const hrefFinesPage = fineId
    ? getLinkHref({ type: 'fines-page', data: { areaId: data.area?.id, number: carNumber } })
    : ''
  const isErrorApiStatus = status === 'error' || status === 'canceled' || status === 'expired'
  const isExhaustedStatus = status === 'exhausted'

  const goToMainPage = () => {
    router.push(hrefMainPage)
  }

  const goToFindCarPage = () => {
    if (hrefSubscriptionFindCar) {
      router.push(hrefSubscriptionFindCar)
      return
    }

    if (hrefEventFindCar) {
      router.push(hrefEventFindCar)
      return
    }

    if (hrefFinesPage) {
      if (!carNumber) {
        router.push(hrefMainPage)
      } else {
        router.push(hrefFinesPage)
      }

      return
    }

    router.push(hrefFindCar)
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    await dispatch(
      checkPayment({
        token: orderNumber,
        onSuccess: ({ status, formUrl }) => {
          setStatus(status)
          formUrl && setFormUrl(formUrl)
        },
        onError: status => {
          setStatus(status)
        },
      }),
    )
  }, [])

  useFetchPaymentStatus({ fetchData, status, setStatus, delay: DELAY_PAYMENT_PENDING, maxCountRequest: COUNT_REQUEST_PAYMENT_PENDING, })

  useEffect(() => {
    if (formUrl) {
      router.push(formUrl)
    }
  }, [formUrl])

  useEffect(() => {
    if (status !== 'pending' && status !== 'creating') {
      setLoading(false)
    }
  }, [status])

  return (
    <>
      {loading && (
        <div className={styles.containerLoader}>
          <div className={styles.spinner}>
            <Icon name={'donut-spinner'} />
            <h2 className={styles.loaderTitle}>
              Платёж обрабатывается. Пожалуйста, оставайтесь на странице.
            </h2>
          </div>
        </div>
      )}

      {!loading && (
        <section className={styles.section}>
          <Container className={styles.container}>
            <div className={styles.container__inner}>
              <h1 className={styles.title}>{STATUS_PAYMENT_MESSAGES[status]}</h1>
            </div>

            <FixedBottom>
              <Container className={styles.buttons}>
                {isExhaustedStatus && (
                  <Button
                    component="button"
                    className={styles.button}
                    onClick={() => {
                      router.reload()
                    }}
                  >
                    Перезагрузить страницу
                  </Button>
                )}

                {isErrorApiStatus && (
                  <Button component="button" className={styles.button} onClick={goToFindCarPage}>
                    Повторить попытку оплаты
                  </Button>
                )}

                {
                  <Button component="button" className={styles.button} onClick={goToMainPage}>
                    На главную
                  </Button>
                }

                <Button
                  component="a"
                  href={supportPhoneLink}
                  className={styles.button}
                  isTransparent
                >
                  Обратиться в поддержку
                </Button>
              </Container>
            </FixedBottom>
          </Container>
        </section>
      )}
    </>
  )
}
